import { cloneDeep, range } from 'lodash-es'
import { Feature } from 'ol'
import { getWidth } from 'ol/extent'
import { GeometryCollection, Point } from 'ol/geom'
import LayerGroup from 'ol/layer/Group'
import LayerImage from 'ol/layer/Image'
import LayerTile from 'ol/layer/Tile'
import LayerVector from 'ol/layer/Vector'
import LayerWebGLVector from 'ol/layer/WebGLVector'
import Cluster from 'ol/source/Cluster'
import ImageArcGISRest from 'ol/source/ImageArcGISRest'
import TileArcGISRest from 'ol/source/TileArcGISRest'
import TileWMS from 'ol/source/TileWMS'
import Vector from 'ol/source/Vector'
import XYZ from 'ol/source/XYZ'
import { Style } from 'ol/style'
import TileState from 'ol/TileState'
import TileGrid from 'ol/tilegrid/TileGrid'
import { uid } from '../utilities'
import { createFeatures } from './FeatureService'
import { getProjection } from './ProjectionService'
import { createStyle } from './StyleService'
import { isWebGLSupported } from './webglSupport'

const sources = {
  XYZ,
  TileWMS,
  TileArcGISRest,
  ImageArcGISRest,
  Vector,
  Cluster,
}

class GroupLayer extends LayerGroup {}

// WMS tile loads fail transiently under geoserver load (gsavalik.envir.ee
// evenings: 429/500/timeout — and with the forced crossOrigin the error
// response usually lacks CORS headers, so the image load just dies). OL
// caches the ERROR tile and never retries, leaving the layer silently blank
// while the base map renders. Retry each failed tile a bounded number of
// times; when retries exhaust, flag the source so the map can tell the user
// which layer it was. Opt-in per layer conf (`tileRetry: true`).
//
// Only a VISIBLE layer may raise the flag: a layer the card has switched off
// (a losing planning tier, RED-293) still gets `tileloaderror` for requests
// that were in flight when it was hidden, and its pending retry timers are
// cancelled by resetTileRetry() so nothing keeps reloading into the void.
export const TILE_LOAD_FAILED_KEY = 'tileLoadFailed'
export const TILE_LOAD_FAILED_URL_KEY = 'tileLoadFailedUrl'
const TILE_RETRY_DELAYS_MS = [1000, 2500]
const tileRetryState = new WeakMap()

export function resetTileRetry(source) {
  const state = tileRetryState.get(source)
  if (state) {
    state.attempts.clear()
    state.timers.forEach(clearTimeout)
    state.timers.clear()
  }
  source.set(TILE_LOAD_FAILED_KEY, false)
}

function retryState(source) {
  const state = { attempts: new Map(), timers: new Set() }
  tileRetryState.set(source, state)
  return state
}

function scheduleRetry(state, delay, callback) {
  const timer = setTimeout(() => {
    state.timers.delete(timer)
    callback()
  }, delay)
  state.timers.add(timer)
}

function flagFailed(source, url) {
  source.set(TILE_LOAD_FAILED_URL_KEY, url ?? null)
  source.set(TILE_LOAD_FAILED_KEY, true)
}

function attachTileRetry(source, layer) {
  const state = retryState(source)
  source.on('tileloaderror', event => {
    if (!layer.getVisible()) {
      return
    }
    const tile = event.tile
    const key = tile.getTileCoord().join('/')
    const attempt = state.attempts.get(key) ?? 0
    if (attempt < TILE_RETRY_DELAYS_MS.length) {
      state.attempts.set(key, attempt + 1)
      scheduleRetry(state, TILE_RETRY_DELAYS_MS[attempt], () => {
        if (layer.getVisible() && tile.getState() === TileState.ERROR) {
          tile.load()
        }
      })
    } else {
      flagFailed(source, tile.getKey?.())
    }
  })
  source.on('tileloadend', event => {
    state.attempts.delete(event.tile.getTileCoord().join('/'))
  })
}

// Same contract for single-image ArcGIS sources (one export per viewport):
// the whole image is the unit, a retry is a source refresh.
function attachImageRetry(source, layer) {
  const state = retryState(source)
  source.on('imageloaderror', event => {
    if (!layer.getVisible()) {
      return
    }
    const attempt = state.attempts.get('image') ?? 0
    if (attempt < TILE_RETRY_DELAYS_MS.length) {
      state.attempts.set('image', attempt + 1)
      scheduleRetry(state, TILE_RETRY_DELAYS_MS[attempt], () => {
        if (layer.getVisible()) {
          source.refresh()
        }
      })
    } else {
      flagFailed(source, event.image?.getImage?.()?.src ?? source.getUrl?.())
    }
  })
  source.on('imageloadend', () => {
    state.attempts.delete('image')
  })
}

class TileLayer extends LayerTile {
  constructor(opts) {
    const sourceOpts = {}
    if (opts.type === 'TileWMS' && opts.tileSize && opts.tileSize !== 256) {
      sourceOpts.tileGrid = tileGridWMS(opts)
    }
    const config = Object.assign({}, opts, sourceOpts)
    config.crossOrigin = 'Anonymous'
    delete config.maxResolution
    delete config.minResolution
    const source = sources[opts.type] ? new sources[opts.type](config) : null
    super({ source })
    if (source && opts.type === 'TileWMS' && opts.tileRetry) {
      attachTileRetry(source, this)
    }
  }
}

// Single-image source for non-cached ArcGIS MapServer endpoints. One export
// request per viewport instead of N tiles — required for upstreams like
// gis.egt.ee that rate-limit aggressively (HTTP 429) under tile fan-out.
class ImageLayer extends LayerImage {
  constructor(opts) {
    const config = { ...opts }
    config.crossOrigin = 'Anonymous'
    delete config.maxResolution
    delete config.minResolution
    const source = sources[opts.type] ? new sources[opts.type](config) : null
    super({ source })
    if (source && opts.tileRetry) {
      attachImageRetry(source, this)
    }
  }
}

class FeatureLayer extends LayerVector {
  constructor(opts) {
    const features = opts.features ? createFeatures(opts) : []
    const source = new Vector({
      features,
    })

    super({
      source,
      style: opts.style,
      updateWhileAnimating: opts.updateWhileAnimating,
      updateWhileInteracting: opts.updateWhileInteracting,
    })
  }
}

class WebGLFeatureLayer extends LayerWebGLVector {
  constructor(opts) {
    const features = opts.features ? createFeatures(opts) : [];
    let source = new Vector({ features });

    if (opts.clusterDistance) {
      source = new Cluster({
        distance: opts.clusterDistance,
        minDistance: opts.clusterMinDistance || 0,
        source,
      });
    }

    super({
      source,
      style: opts.style,
      variables: opts.variables || {},
      filter: opts.filter,
      disableHitDetection: opts.clusterDistance ? false : undefined, // Enable for clusters
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
  }
}

class ClusterLayer extends LayerVector {
  constructor(opts) {
    const features = opts.features ? createFeatures(opts) : []
    const source = new Cluster({
      distance: opts.clusterDistance || 25,
      source: new Vector({
        features,
      }),
      geometryFunction: feature => {
        let geom = null
        if (feature.getGeometry() instanceof Point) {
          geom = feature.getGeometry()
        } else if (feature.getGeometry() instanceof GeometryCollection) {
          geom = feature
            .getGeometry()
            .getGeometries()
            .find(g => g instanceof Point)
        }
        return geom
      },
      createCluster: (point, features) => {
        return new Feature({
          geometry: features.length > 1 ? point : features[0].getGeometry(),
          features: features,
        })
      },
    })
    super({
      source,
      style: opts.style,
      updateWhileAnimating: opts.updateWhileAnimating,
      updateWhileInteracting: opts.updateWhileInteracting,
    })
  }
}

export const createLayer = layerConf => {
  let layer
  switch (layerConf.type) {
    case 'Group': {
      const arr = layerConf.layers.map(conf => {
        const inputConf = cloneDeep(conf)
        // add projection to sublayer
        if (!inputConf.projection) {
          inputConf.projection = layerConf.projection
        }
        if (!inputConf.zIndex) {
          inputConf.zIndex = layerConf.zIndex
        }
        return createLayer(inputConf)
      })
      // group should be visible, if not specified
      if (typeof layerConf.visible === 'undefined') {
        layerConf.visible = true
      }
      layer = new GroupLayer({
        layers: arr,
      })
      break
    }
    case 'XYZ':
    case 'TileWMS':
    case 'TileArcGISRest':
      layer = new TileLayer(cloneDeep(layerConf))
      break
    case 'ImageArcGISRest':
      layer = new ImageLayer(cloneDeep(layerConf))
      break
    case 'FeatureCollection': {
      let styleFn = null
      if (
        typeof layerConf.style === 'function' ||
        layerConf.style instanceof Style
      ) {
        styleFn = layerConf.style
      }
      const clone = cloneDeep(layerConf)
      if (styleFn) {
        clone.style = styleFn
      } else {
        clone.style = createStyle(clone.style)
      }

      // If webgl is specified (and the browser supports it), use the WebGL layer
      if (clone.webgl && isWebGLSupported()) {
        layer = new WebGLFeatureLayer(clone)
      } else {
        layer = clone.clusters
          ? new ClusterLayer(clone)
          : new FeatureLayer(clone)
      }
      break
    }
  }
  if (!layer) {
    return false
  }
  set(layer, layerConf)
  return layer
}

function set(layer, layerConf) {
  if (typeof layerConf.visible === 'boolean') {
    layer.setVisible(layerConf.visible)
  }
  if (typeof layerConf.id === 'undefined') {
    layerConf.id = uid()
  }
  layer.set('id', layerConf.id)
  if (layerConf.label) {
    layer.set('title', layerConf.label)
  }
  if (layerConf.layerType) {
    layer.set('type', layerConf.type)
  }
  if (layerConf.icon) {
    layer.set('icon', layerConf.icon)
  }
  if (layerConf.minResolution) {
    layer.setMinResolution(layerConf.minResolution)
  }
  if (layerConf.maxResolution) {
    layer.setMaxResolution(layerConf.maxResolution)
  }
  if (typeof layerConf.opacity !== 'undefined') {
    layer.setOpacity(layerConf.opacity)
  }
  // Render extent in map coordinates — sources whose data covers only a
  // region (e.g. Tallinn's city GIS) skip requests everywhere else.
  if (Array.isArray(layerConf.extent) && layerConf.extent.length === 4) {
    layer.setExtent(layerConf.extent)
  }
  if (typeof layerConf.zIndex !== 'undefined') {
    layer.setZIndex(layerConf.zIndex)
  } else {
    layer.setZIndex(100)
  }
  if (layerConf.clusters) {
    layer.set('clusters', layerConf.clusters)
  }
  layer.set('conf', layerConf)
  return layer
}

function tileGridWMS(opts) {
  const projExtent = getProjection(opts.projection).getExtent()
  const startResolution = getWidth(projExtent) / opts.tileSize
  const resolutions = range(opts.minZoom, opts.maxZoom + 1)
  for (let i = 0, ii = resolutions.length; i < ii; ++i) {
    resolutions[i] = startResolution / 2 ** i
  }
  return new TileGrid({
    extent: projExtent,
    resolutions: resolutions,
    tileSize: [opts.tileSize, opts.tileSize],
  })
}
